import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, SEASON_LABELS } from "../lib/api";
import { SectionHeader } from "./Home";
import { Filter } from "lucide-react";

export default function Episodes() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const seasonParam = searchParams.get("season");
  const selectedSeason = seasonParam ? parseInt(seasonParam, 10) : "all";

  useEffect(() => {
    setLoading(true);
    const url = selectedSeason === "all" ? "/episodes" : `/episodes?season=${selectedSeason}`;
    api.get(url)
      .then((r) => setEpisodes(r.data))
      .catch(() => setEpisodes([]))
      .finally(() => setLoading(false));
  }, [selectedSeason]);

  const groups = useMemo(() => {
    const map = {};
    episodes.forEach((ep) => {
      if (!map[ep.season]) map[ep.season] = [];
      map[ep.season].push(ep);
    });
    return map;
  }, [episodes]);

  const setSeason = (val) => {
    if (val === "all") setSearchParams({});
    else setSearchParams({ season: String(val) });
  };

  return (
    <div data-testid="episodes-page">
      <SectionHeader
        title="Episode Index"
        subtitle={`${episodes.length} episodes · Pick one to dive in`}
      />

      {/* Filter bar */}
      <div className="brutal-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="filter-bar">
        <div className="flex items-center gap-2 font-mono-display text-xs uppercase tracking-widest text-slate-600">
          <Filter size={14} strokeWidth={3} /> Filter:
        </div>
        <FilterChip
          label="All"
          active={selectedSeason === "all"}
          onClick={() => setSeason("all")}
          testid="filter-all"
        />
        {Object.keys(SEASON_LABELS).map((n) => (
          <FilterChip
            key={n}
            label={`S${n}`}
            active={String(selectedSeason) === n}
            onClick={() => setSeason(n)}
            testid={`filter-season-${n}`}
          />
        ))}
      </div>

      {loading ? (
        <div className="brutal-card p-10 text-center font-mono-display uppercase tracking-widest" data-testid="episodes-loading">
          Loading the hatch...
        </div>
      ) : (
        Object.keys(groups)
          .sort((a, b) => Number(a) - Number(b))
          .map((season) => (
            <div key={season} className="mb-8" data-testid={`season-group-${season}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-slate-900 text-white font-mono-display text-xs uppercase tracking-widest px-3 py-1.5">
                  {SEASON_LABELS[season]}
                </div>
                <div className="h-[2px] flex-1 bg-slate-900" />
                <div className="font-mono-display text-xs uppercase tracking-widest text-slate-600">
                  {groups[season].length} eps
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {groups[season].map((ep) => (
                  <EpisodeCard key={ep.id} ep={ep} />
                ))}
              </div>
            </div>
          ))
      )}
    </div>
  );
}

function EpisodeCard({ ep }) {
  return (
    <Link
      to={`/episodes/${ep.id}`}
      className="brutal-card p-5 block no-underline text-slate-900 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:bg-yellow-50 transition-transform"
      data-testid={`episode-card-${ep.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="bg-pink-500 text-white brutal-border font-mono-display font-bold text-xl min-w-[56px] min-h-[56px] flex items-center justify-center">
          {String(ep.episode_number).padStart(2, "0")}
        </div>
        <div className="flex-1">
          <div className="font-mono-display text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">
            S{ep.season} · EP {ep.episode_number} · {ep.air_date}
          </div>
          <div className="font-bold text-lg leading-tight mb-1">{ep.title}</div>
          <div className="text-sm text-slate-700 line-clamp-2">{ep.synopsis}</div>
        </div>
      </div>
    </Link>
  );
}

function FilterChip({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`brutal-btn ${active ? "primary" : ""}`}
    >
      {label}
    </button>
  );
}
